/**
 * Owner: Masa Bayu
 * File: pdf-glyph-widths.ts
 * Date:Time: 2026-10-01 08:10
 * Description: Resolve PDF text advance widths · embedded /Widths or Standard 14 AFM.
 */

export type QXPdfGlyphWidths = {
  readonly firstChar: number;
  readonly widths: readonly number[];
  /** embedded = from PDF dict; standard-14 = AFM when Widths omitted (PDF-legal). */
  readonly source: 'embedded' | 'standard-14';
};

/** Adobe Helvetica WinAnsi widths for codes 32…126 (Standard 14). */
const HELVETICA_32_126: readonly number[] = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833,
  722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556,
  556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334,
  260, 334, 584,
];

const HELVETICA_BOLD_32_126: readonly number[] = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 556, 333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833,
  722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611,
  556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389,
  280, 389, 584,
];

const TIMES_ROMAN_32_126: readonly number[] = [
  250, 333, 408, 500, 500, 833, 778, 180, 333, 333, 500, 564, 250, 333, 250, 278, 500, 500, 500, 500, 500, 500, 500,
  500, 500, 500, 278, 278, 564, 564, 564, 444, 921, 722, 667, 667, 722, 611, 556, 722, 722, 333, 389, 722, 611, 889,
  722, 722, 556, 722, 667, 556, 611, 722, 722, 944, 722, 722, 611, 333, 278, 333, 469, 500, 333, 444, 500, 444, 500,
  444, 333, 500, 500, 278, 278, 500, 278, 778, 500, 500, 500, 500, 333, 389, 278, 500, 500, 722, 500, 500, 444, 480,
  200, 480, 541,
];

const COURIER_600 = Object.freeze(Array.from({ length: 95 }, () => 600));

const STANDARD_14: ReadonlyMap<string, readonly number[]> = new Map([
  ['Helvetica', HELVETICA_32_126],
  ['Helvetica-Oblique', HELVETICA_32_126],
  ['Helvetica-Bold', HELVETICA_BOLD_32_126],
  ['Helvetica-BoldOblique', HELVETICA_BOLD_32_126],
  ['Times-Roman', TIMES_ROMAN_32_126],
  ['Times-Italic', TIMES_ROMAN_32_126],
  ['Times-Bold', TIMES_ROMAN_32_126],
  ['Times-BoldItalic', TIMES_ROMAN_32_126],
  ['Courier', COURIER_600],
  ['Courier-Oblique', COURIER_600],
  ['Courier-Bold', COURIER_600],
  ['Courier-BoldOblique', COURIER_600],
]);

function pdfName(raw: string): string {
  return raw.replace(/#([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));
}

function parseWidthArray(raw: string | undefined): number[] {
  return (raw ?? '').trim().split(/\s+/).filter(Boolean).map(Number);
}

/**
 * Eyes needs glyph advances for text bbox. Prefer embedded /Widths.
 * When omitted, PDF Standard 14 BaseFonts may use AFM metrics (not invented text).
 * Non-standard fonts without Widths remain a hard reject.
 */
export function resolvePdfFontGlyphWidths(
  fontDict: string,
  readWidthsObject: (objectId: number) => string,
): QXPdfGlyphWidths {
  const firstEmbedded = Number(fontDict.match(/\/FirstChar\s+(\d+)/)?.[1] ?? Number.NaN);
  const widthsId = Number(fontDict.match(/\/Widths\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  const inline = fontDict.match(/\/Widths\s*\[([^\]]*)\]/)?.[1];

  if (Number.isInteger(firstEmbedded) && widthsId) {
    const widths = parseWidthArray(readWidthsObject(widthsId).match(/\[([^\]]*)\]/)?.[1]);
    if (widths.length === 0) throw new Error('PDF font has no glyph widths');
    return { firstChar: firstEmbedded, widths, source: 'embedded' };
  }
  if (Number.isInteger(firstEmbedded) && inline !== undefined) {
    const widths = parseWidthArray(inline);
    if (widths.length === 0) throw new Error('PDF font has no glyph widths');
    return { firstChar: firstEmbedded, widths, source: 'embedded' };
  }

  const base = fontDict.match(/\/BaseFont\s*\/([^\s/]+)/)?.[1];
  if (!base) throw new Error('PDF font has no BaseFont');
  const fontName = pdfName(base);
  const standard = STANDARD_14.get(fontName);
  if (!standard) throw new Error('PDF font has no glyph widths');
  return { firstChar: 32, widths: standard, source: 'standard-14' };
}

/** Sum advance units for one showing; missing glyph stays a hard reject. */
export function advanceUnitsForText(glyph: QXPdfGlyphWidths, text: string): number {
  let units = 0;
  for (const char of text) {
    const width = glyph.widths[char.charCodeAt(0) - glyph.firstChar];
    if (width === undefined || width <= 0) throw new Error(`PDF glyph width missing for ${char}`);
    units += width;
  }
  return units;
}
